import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Paper, Button, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Chip, InputAdornment, Alert, CircularProgress,
  Checkbox, TablePagination, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import BlockIcon from '@mui/icons-material/Block'
import AddIcon from '@mui/icons-material/Add'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import { labelReason } from './labels'
import { normalizePhone, nationalBrPhone, parseImport, groupByName } from './listImport'

const sf = hipersendService.smartFilter

function usesSuppression(rule) {
  if (rule?.templateCode === 'lista-supressao') return true
  return (rule?.conditions?.items || []).some((it) => it.value === 'suppressionList')
}

export default function SuppressionList({ api, secret, rules, onEnsureRule, notify }) {
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [raw, setRaw] = useState('')
  const [notesLabel, setNotesLabel] = useState('')
  const [addDdi, setAddDdi] = useState(true)
  const [importing, setImporting] = useState(false)
  const [search, setSearch] = useState('')
  const [single, setSingle] = useState('')
  const [ensuring, setEnsuring] = useState(false)
  const fileRef = useRef(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selected, setSelected] = useState(() => new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try { setList(await sf.listSuppression(api, secret)) }
    catch (e) { notify('Falha ao carregar a lista de bloqueio') }
    finally { setLoading(false) }
  }, [api, secret, notify])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [search])

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    try { setRaw(await file.text()) } catch (err) { notify('Falha ao ler o arquivo') }
    e.target.value = ''
  }

  const preview = useMemo(() => parseImport(raw, addDdi), [raw, addDdi])
  const suppressionRuleActive = useMemo(
    () => (rules || []).some((r) => r.enabled && usesSuppression(r)),
    [rules]
  )

  const groups = useMemo(() => groupByName(list), [list])
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase()
    const qDigits = normalizePhone(search)
    if (!q) return groups
    return groups.filter(
      (g) => (g.name && g.name.toLowerCase().includes(q)) || (qDigits && g.items.some((it) => String(it.phone).includes(qDigits)))
    )
  }, [groups, search])

  const maxPage = Math.max(0, Math.ceil(filteredGroups.length / rowsPerPage) - 1)
  const safePage = Math.min(page, maxPage)
  const pageGroups = filteredGroups.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)
  const phonesOnPage = pageGroups.flatMap((g) => g.items.map((it) => it.phone))
  const allPageSelected = phonesOnPage.length > 0 && phonesOnPage.every((p) => selected.has(p))
  const somePageSelected = phonesOnPage.some((p) => selected.has(p)) && !allPageSelected

  const togglePhones = (phones, on) => {
    setSelected((prev) => {
      const next = new Set(prev)
      phones.forEach((p) => (on ? next.add(p) : next.delete(p)))
      return next
    })
  }
  const toggleSelectAllPage = () => togglePhones(phonesOnPage, !allPageSelected)
  const toggleGroup = (g) => {
    const phones = g.items.map((it) => it.phone)
    togglePhones(phones, !phones.every((p) => selected.has(p)))
  }

  const doImport = async () => {
    if (!preview.valid) return
    setImporting(true)
    try {
      const r = await sf.bulkAddSuppression(api, secret, preview.items, { reason: 'manual', notes: notesLabel || undefined })
      // Quem já pediu para não receber fica como está — o import não rebaixa
      // um opt-out real para bloqueio manual (que só vale com o filtro ligado).
      const mantidos = r?.optOutsPreservados || 0
      notify(`Importados: ${r?.added || 0} novos · ${r?.updated || 0} atualizados${mantidos ? ` · ${mantidos} já tinham pedido para não receber (mantidos)` : ''}`)
      setRaw('')
      await load()
    } catch (e) { notify('Falha ao importar a lista') }
    finally { setImporting(false) }
  }

  const addSingle = async () => {
    const nat = nationalBrPhone(single)
    const phone = nat ? (addDdi ? '55' + nat : nat) : normalizePhone(single)
    if (!phone || phone.length < 8) { notify('Número inválido'); return }
    try {
      const r = await sf.addSuppression(api, secret, { phone, reason: 'manual', notes: notesLabel || undefined })
      setSingle('')
      await load()
      notify(r?.optOutPreservado ? 'Esse número já tinha pedido para não receber — mantido como opt-out' : 'Número adicionado')
    } catch (e) { notify('Falha ao adicionar o número') }
  }

  const removeOne = async (phone) => {
    try {
      await sf.removeSuppression(api, secret, phone)
      setSelected((prev) => { const n = new Set(prev); n.delete(phone); return n })
      await load()
    } catch (e) { notify('Falha ao remover o número') }
  }

  const doBulkDelete = async () => {
    setDeleting(true)
    try {
      const phones = [...selected]
      await sf.bulkRemoveSuppression(api, secret, phones)
      notify(`${phones.length} número(s) removido(s)`)
      setSelected(new Set())
      setConfirmOpen(false)
      await load()
    } catch (e) { notify('Falha ao excluir em massa') }
    finally { setDeleting(false) }
  }

  const ensureRule = async () => {
    setEnsuring(true)
    try { await onEnsureRule(); notify('Bloqueio da lista ativado') }
    catch (e) { notify('Falha ao ativar o bloqueio da lista') }
    finally { setEnsuring(false) }
  }

  return (
    <div className="space-y-4">
      {!loading && list.length > 0 && !suppressionRuleActive && (
        <Alert
          severity="warning"
          action={<Button color="inherit" size="small" startIcon={<BlockIcon />} disabled={ensuring} onClick={ensureRule}>Ativar bloqueio da lista</Button>}
        >
          Você tem <b>{list.length}</b> número(s) na lista, mas a regra que bloqueia eles <b>não está ativa</b> — sem isso, nada é filtrado.
        </Alert>
      )}
      {!loading && list.length > 0 && suppressionRuleActive && (
        <Alert severity="success">Bloqueio da lista <b>ativo</b> — mensagens para esses números são descartadas.</Alert>
      )}

      <Paper variant="outlined" className="p-4" sx={{ borderRadius: '16px' }}>
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <Text className="block font-semibold">Importar em massa</Text>
          <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current && fileRef.current.click()}>
            Carregar arquivo (.csv / .txt)
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" hidden onChange={onFile} />
        </div>
        <Text className="block text-xs opacity-70 mb-3">
          Cole uma lista, uma coluna, ou um CSV inteiro (SGP, IXC, ISPFY…) — a gente acha o telefone <b>e o nome</b> sozinho,
          ignorando CNPJ, CEP, datas e outros campos. Dica: exporte os contratos bloqueados/comodato e cole ou carregue aqui.
        </Text>
        <TextField
          fullWidth multiline minRows={4} maxRows={12} size="small"
          placeholder={'Cole aqui...\nNome,Celular,Tipo\nMARIA SILVA,27999998888,WhatsApp\n(27) 99840-9474'}
          value={raw} onChange={(e) => setRaw(e.target.value)}
          InputProps={{ style: { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 } }}
        />
        <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-sm">
              {raw.trim() ? (
                <>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>{preview.valid} válidos</span>
                  {preview.duplicates > 0 && <span style={{ color: '#b54708' }}> · {preview.duplicates} duplicados</span>}
                  {preview.invalid > 0 && <span style={{ color: '#b42318' }}> · {preview.invalid} ignorados</span>}
                </>
              ) : <span className="opacity-50">Cole/carregue para ver a prévia</span>}
            </div>
            <FormControlLabel
              control={<Switch size="small" checked={addDdi} onChange={(e) => setAddDdi(e.target.checked)} />}
              label={<span className="text-sm">Adicionar 55 quando faltar</span>}
            />
          </div>
          <div className="flex items-center gap-2">
            <TextField size="small" label="Etiqueta (opcional)" placeholder="ex: comodato" value={notesLabel} onChange={(e) => setNotesLabel(e.target.value)} style={{ width: 170 }} />
            <Button variant="contained" startIcon={<UploadFileIcon />} disabled={!preview.valid || importing} onClick={doImport}>
              {importing ? 'Importando…' : `Importar ${preview.valid || ''} número(s)`}
            </Button>
          </div>
        </div>
      </Paper>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <TextField
            size="small" placeholder="Buscar nome ou número" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            style={{ width: 240 }}
          />
          {selected.size > 0 && (
            <Button color="error" variant="contained" size="small" startIcon={<DeleteIcon />} onClick={() => { setConfirmText(''); setConfirmOpen(true) }}>
              Excluir {selected.size} selecionado(s)
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TextField
            size="small" placeholder="Adicionar um número" value={single}
            onChange={(e) => setSingle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addSingle() }}
            style={{ width: 190 }}
          />
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addSingle}>Adicionar</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress size={24} /></div>
      ) : !list.length ? (
        <Text className="block text-sm opacity-60 py-4">Nenhum número na lista ainda. Importe acima.</Text>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '16px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={allPageSelected} indeterminate={somePageSelected} onChange={toggleSelectAllPage} />
                </TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Números</TableCell>
                <TableCell>Etiqueta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageGroups.map((g) => {
                const phones = g.items.map((it) => it.phone)
                const allSel = phones.every((p) => selected.has(p))
                const someSel = phones.some((p) => selected.has(p)) && !allSel
                const first = g.items[0] || {}
                return (
                  <TableRow key={g.key} hover>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSel} indeterminate={someSel} onChange={() => toggleGroup(g)} />
                    </TableCell>
                    <TableCell>
                      {g.name || <span className="opacity-40">—</span>}
                      {g.items.length > 1 && <span className="text-xs opacity-50"> · {g.items.length} números</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {g.items.map((it) => (
                          <Chip key={it.phone} size="small" label={it.phone} onDelete={() => removeOne(it.phone)} style={{ fontFamily: 'ui-monospace, monospace' }} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={first.notes || labelReason(first.reason)} /></TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredGroups.length}
            page={safePage}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count} nome(s)`}
          />
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
        <DialogTitle>Excluir em massa</DialogTitle>
        <DialogContent>
          <Text className="block mb-3 text-sm">
            Isso remove <b>{selected.size}</b> número(s) da lista de bloqueio. Para confirmar, digite <b>EXCLUIR</b> abaixo.
          </Text>
          <TextField autoFocus fullWidth size="small" placeholder="EXCLUIR" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={confirmText.trim().toUpperCase() !== 'EXCLUIR' || deleting} onClick={doBulkDelete}>
            {deleting ? 'Excluindo…' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
